import { ArrowUpRight, ExternalLink } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { PORTFOLIO_ITEMS } from "../data/content";

const LiveCard = ({ item }) => (
  <a
    href={item.link}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={`portfolio-card-${item.id}`}
    className="card-lift group relative block h-full min-h-[300px] overflow-hidden rounded-3xl border border-gold/30"
  >
    <img
      src={item.image}
      alt={`${item.title} — ${item.category} by Faithh Business Consultancy`}
      className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-105"
      loading="lazy"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-navy-950/95 via-navy-950/45 to-navy-950/15 transition-opacity duration-500" />
    <span className="absolute right-5 top-5 flex items-center gap-2 rounded-full border border-gold/40 bg-navy-950/80 px-3.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.24em] text-gold-soft backdrop-blur-sm">
      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
      Live Project
    </span>
    <div className="absolute inset-x-0 bottom-0 p-7">
      <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold-soft">{item.category}</p>
      <h3 className="mt-2 font-display text-2xl font-semibold text-slate-50">{item.title}</h3>
      <p className="mt-1.5 text-xs leading-relaxed text-slate-400">{item.blurb}</p>
      <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-gold-soft opacity-0 transition-all duration-500 group-hover:opacity-100">
        Visit Site
        <ExternalLink className="h-4 w-4" />
      </span>
    </div>
  </a>
);

const PlaceholderCard = ({ item }) => {
  const Icon = item.icon;
  return (
    <article
      data-testid={`portfolio-card-${item.id}`}
      className="card-lift group relative flex h-full min-h-[280px] flex-col justify-between overflow-hidden rounded-3xl border border-white/8 bg-navy-850/80 p-8"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(212,175,55,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.6) 1px, transparent 1px)",
          backgroundSize: "36px 36px",
        }}
      />
      <span className="absolute right-5 top-5 rounded-full border border-gold/30 bg-navy-950/70 px-3.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.24em] text-gold-soft">
        Coming Soon
      </span>
      <Icon className="relative h-10 w-10 text-gold/50 transition-all duration-500 group-hover:scale-110 group-hover:text-gold" />
      <div className="relative">
        <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-slate-500">{item.category}</p>
        <h3 className="mt-2 font-display text-2xl font-semibold text-slate-100">{item.title}</h3>
      </div>
    </article>
  );
};

export const Portfolio = () => (
  <section id="work" data-testid="portfolio-section" className="noise-overlay relative bg-navy-900 py-24 lg:py-32">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(212,175,55,0.05),transparent_50%)]" />
    <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
      <SectionHeading
        overline="Our Work"
        testId="portfolio-heading"
        title={
          <>
            A showcase we're <span className="text-gold-metallic italic pr-2 -mr-2">building with pride</span>
          </>
        }
        description="Real projects, crafted end-to-end by Faithh — starting with Bright Future Textile, a premium tailoring brand in the UAE. More websites, brands and campaigns are on their way."
      />

      <div className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-7">
        {PORTFOLIO_ITEMS.map((item, i) => {
          const isCta = item.id === "work-more";
          return (
            <Reveal key={item.id} delay={(i % 3) * 0.08} className="h-full">
              {isCta ? (
                <a
                  href="#contact"
                  data-testid="portfolio-cta-card"
                  className="card-lift group flex h-full min-h-[280px] flex-col items-center justify-center gap-4 rounded-3xl border border-dashed border-gold/35 bg-gold/[0.04] p-8 text-center transition-colors duration-500 hover:bg-gold/[0.09]"
                >
                  <span className="flex h-14 w-14 items-center justify-center rounded-full border border-gold/40 bg-gold/10 transition-transform duration-500 group-hover:scale-110">
                    <ArrowUpRight className="h-6 w-6 text-gold" />
                  </span>
                  <span className="font-display text-2xl font-semibold text-slate-50">{item.title}</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold-soft">{item.category}</span>
                </a>
              ) : item.live ? (
                <LiveCard item={item} />
              ) : (
                <PlaceholderCard item={item} />
              )}
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);
